import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SectionBar } from "@/components/ui/section-bar";
import { useClaimMaintenanceUsers } from "@/hooks/use-claim-maintenance-users";
import { useUndoableDelete } from "@/hooks/use-undoable-delete";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { teamKeys } from "@/lib/query";
import { cn } from "@/lib/utils";
import {
  type ClaimRegion,
  type ClaimTeam,
  claimRegionArraySchema,
  claimTeamArraySchema,
  teamMemberArraySchema,
} from "@/schemas/teams";

const MANAGER_LIST_ID = "claim-maintenance-managers";
const USER_LIST_ID = "claim-maintenance-users";

function CheckCell({ value }: { value: boolean }) {
  return value ? (
    <Check className="size-4 text-primary" />
  ) : (
    <span className="text-muted-foreground/40">—</span>
  );
}

// ── Component ──

export function ClaimTeamMaintenancePage() {
  const queryClient = useQueryClient();

  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const [showAddRegion, setShowAddRegion] = useState(false);
  const [editingRegionId, setEditingRegionId] = useState<string | null>(null);
  const [regionForm, setRegionForm] = useState({
    claimRegion: "",
    sortOrder: "0",
    isManagementGroup: false,
    isBackupHistory: false,
  });

  const [showAddTeam, setShowAddTeam] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [teamForm, setTeamForm] = useState({
    claimRegionTeam: "",
    manager: "",
    sortOrder: "0",
    isClaimTeam: true,
  });

  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState({ userId: "" });

  // Confirm-dialog targets — one per delete kind so the title / description
  // can be tailored to each entity ("Delete claims group" vs "Remove member").
  const [pendingRegionDelete, setPendingRegionDelete] = useState<ClaimRegion | null>(null);
  const [pendingTeamDelete, setPendingTeamDelete] = useState<ClaimTeam | null>(null);
  const [pendingMemberDelete, setPendingMemberDelete] = useState<{
    id: string;
    userId: string;
  } | null>(null);

  // ── Queries ──

  const regionsQuery = useQuery({
    queryKey: teamKeys.regions,
    queryFn: () => apiGet("/api/claim-regions", claimRegionArraySchema),
  });

  const teamsQuery = useQuery({
    queryKey: teamKeys.teams(selectedRegionId ?? ""),
    queryFn: () => apiGet(`/api/claim-regions/${selectedRegionId}/teams`, claimTeamArraySchema),
    enabled: selectedRegionId !== null,
  });

  const membersQuery = useQuery({
    queryKey: teamKeys.users(selectedTeamId ?? ""),
    queryFn: () => apiGet(`/api/claim-teams/${selectedTeamId}/users`, teamMemberArraySchema),
    enabled: selectedTeamId !== null,
  });

  const maintenanceUsersQuery = useClaimMaintenanceUsers();
  const allUsers = maintenanceUsersQuery.data ?? [];
  const existingMemberIds = new Set(membersQuery.data?.map((m) => m.userId.toUpperCase()) ?? []);
  const userPickerOptions = allUsers.filter(
    (u) => !existingMemberIds.has(u.samAccountName.toUpperCase()),
  );

  const selectedRegion = regionsQuery.data?.find((r) => r.id === selectedRegionId) ?? null;

  // Auto-select the first team when a region's teams load and no team is selected
  // (or the currently selected team isn't in the new list).
  useEffect(() => {
    const teams = teamsQuery.data;
    if (!teams || teams.length === 0) {
      if (selectedTeamId !== null) setSelectedTeamId(null);
      return;
    }
    if (!selectedTeamId || !teams.some((t) => t.id === selectedTeamId)) {
      setSelectedTeamId(teams[0]?.id ?? null);
    }
  }, [teamsQuery.data, selectedTeamId]);

  // ── Region Mutations ──

  const addRegionMutation = useMutation({
    mutationFn: (data: {
      claimRegion: string;
      sortOrder: number;
      isManagementGroup: boolean;
      isBackupHistory: boolean;
    }) => apiSend("POST", "/api/claim-regions", data, z.unknown()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.regions });
      toast.success("Claims group added");
      setShowAddRegion(false);
      setRegionForm({
        claimRegion: "",
        sortOrder: "0",
        isManagementGroup: false,
        isBackupHistory: false,
      });
    },
    onError: (error) => {
      logger.error("claim_region_add_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to add claims group");
    },
  });

  const updateRegionMutation = useMutation({
    mutationFn: (data: {
      id: string;
      claimRegion: string;
      sortOrder: number;
      isManagementGroup: boolean;
      isBackupHistory: boolean;
    }) =>
      apiSend(
        "PUT",
        `/api/claim-regions/${data.id}`,
        {
          claimRegion: data.claimRegion,
          sortOrder: data.sortOrder,
          isManagementGroup: data.isManagementGroup,
          isBackupHistory: data.isBackupHistory,
        },
        z.unknown(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.regions });
      toast.success("Claims group updated");
      setEditingRegionId(null);
    },
    onError: (error) => {
      logger.error("claim_region_update_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to update claims group");
    },
  });

  const { pendingIds: pendingRegionIds, enqueue: enqueueRegionDelete } =
    useUndoableDelete<ClaimRegion>({
      describe: (r) => `Deleted group "${r.claimRegion}"`,
      commit: (r) => apiSend("DELETE", `/api/claim-regions/${r.id}`, undefined, z.unknown()),
      onCommitSuccess: () => {
        queryClient.invalidateQueries({ queryKey: teamKeys.regions });
      },
      onCommitError: (_, error) => {
        logger.error("claim_region_delete_failed", { error });
      },
    });

  // ── Team Mutations ──

  const addTeamMutation = useMutation({
    mutationFn: (data: {
      claimRegionTeam: string;
      manager: string;
      sortOrder: number;
      isClaimTeam: boolean;
      claimRegionId: string;
    }) => apiSend("POST", `/api/claim-regions/${data.claimRegionId}/teams`, data, z.unknown()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.teams(selectedRegionId ?? "") });
      toast.success("Team added");
      setShowAddTeam(false);
      setTeamForm({ claimRegionTeam: "", manager: "", sortOrder: "0", isClaimTeam: true });
    },
    onError: (error) => {
      logger.error("claim_team_add_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to add team");
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: (data: {
      id: string;
      claimRegionTeam: string;
      manager: string;
      sortOrder: number;
      isClaimTeam: boolean;
    }) =>
      apiSend(
        "PUT",
        `/api/claim-teams/${data.id}`,
        {
          claimRegionTeam: data.claimRegionTeam,
          manager: data.manager,
          sortOrder: data.sortOrder,
          isClaimTeam: data.isClaimTeam,
        },
        z.unknown(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.teams(selectedRegionId ?? "") });
      toast.success("Team updated");
      setEditingTeamId(null);
    },
    onError: (error) => {
      logger.error("claim_team_update_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to update team");
    },
  });

  const { pendingIds: pendingTeamIds, enqueue: enqueueTeamDelete } = useUndoableDelete<ClaimTeam>({
    describe: (t) => `Deleted team "${t.claimRegionTeam}"`,
    commit: (t) => apiSend("DELETE", `/api/claim-teams/${t.id}`, undefined, z.unknown()),
    onCommitSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.teams(selectedRegionId ?? "") });
    },
    onCommitError: (_, error) => {
      logger.error("claim_team_delete_failed", { error });
    },
  });

  // ── Member Mutations ──

  const addUserMutation = useMutation({
    mutationFn: (data: { userId: string; claimRegionTeamId: string }) =>
      apiSend("POST", `/api/claim-teams/${data.claimRegionTeamId}/users`, data, z.unknown()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.users(selectedTeamId ?? "") });
      toast.success("Member added");
      setShowAddUser(false);
      setUserForm({ userId: "" });
    },
    onError: (error) => {
      logger.error("claim_team_member_add_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to add member");
    },
  });

  const { pendingIds: pendingMemberIds, enqueue: enqueueMemberDelete } = useUndoableDelete<{
    id: string;
    userId: string;
  }>({
    describe: (m) => `Removed ${m.userId} from this team`,
    commit: (m) => apiSend("DELETE", `/api/claim-team-users/${m.id}`, undefined, z.unknown()),
    onCommitSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.users(selectedTeamId ?? "") });
    },
    onCommitError: (_, error) => {
      logger.error("claim_team_member_remove_failed", { error });
    },
  });

  // ── Handlers ──

  function handleSelectRegion(region: ClaimRegion) {
    setSelectedRegionId(region.id);
    setSelectedTeamId(null);
    setShowAddTeam(false);
    setShowAddUser(false);
    setEditingRegionId(null);
    setEditingTeamId(null);
  }

  function handleSelectTeam(team: ClaimTeam) {
    setSelectedTeamId(team.id);
    setShowAddUser(false);
    setEditingTeamId(null);
  }

  function startEditRegion(region: ClaimRegion) {
    setEditingRegionId(region.id);
    setShowAddRegion(false);
    setRegionForm({
      claimRegion: region.claimRegion,
      sortOrder: String(region.sortOrder),
      isManagementGroup: region.isManagementGroup,
      isBackupHistory: region.isBackupHistory,
    });
  }

  function startEditTeam(team: ClaimTeam) {
    setEditingTeamId(team.id);
    setShowAddTeam(false);
    setTeamForm({
      claimRegionTeam: team.claimRegionTeam,
      manager: team.manager,
      sortOrder: String(team.sortOrder),
      isClaimTeam: team.isClaimTeam,
    });
  }

  const groupLabel = selectedRegion?.claimRegion ?? "Claims Group";
  const teamsPanelTitle = selectedRegion
    ? `${selectedRegion.claimRegion} Teams`
    : "Teams (select a claims group)";

  return (
    <div className="space-y-4">
      <PageBreadcrumbs current="Claim Teams" />
      <div className="grid gap-4 lg:grid-cols-12">
        {/* Shared dropdown sources for Manager + User ID inputs.
            Both pull from legacy "User_ActiveDirectory (List) (Claims Maintenance)". */}
        <datalist id={MANAGER_LIST_ID}>
          {allUsers.map((u) => (
            <option
              key={u.samAccountName}
              value={u.firstLastName ?? u.name}
              label={
                u.title ? `${u.firstLastName ?? u.name} — ${u.title}` : (u.firstLastName ?? u.name)
              }
            />
          ))}
        </datalist>
        <datalist id={USER_LIST_ID}>
          {userPickerOptions.map((u) => (
            <option
              key={u.samAccountName}
              value={u.samAccountName}
              label={`${u.firstLastName ?? u.name}${u.title ? ` — ${u.title}` : ""}`}
            />
          ))}
        </datalist>

        {/* ─── LEFT COLUMN ─── */}
        <div className="space-y-4 lg:col-span-4">
          {/* Claims Group */}
          <section>
            <SectionBar title="Claims Group" subtitle={`${regionsQuery.data?.length ?? 0} rows`} />
            <div className="rounded-b-md border border-t-0 bg-card p-3">
              <div className="mb-3 flex flex-wrap gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  onClick={() => {
                    setShowAddRegion(true);
                    setEditingRegionId(null);
                    setRegionForm({
                      claimRegion: "",
                      sortOrder: "0",
                      isManagementGroup: false,
                      isBackupHistory: false,
                    });
                  }}
                >
                  <Plus className="size-3.5" />
                  Create
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  asChild
                >
                  <a href="/api/teams/team-roster.pdf" target="_blank" rel="noopener">
                    Team PDF
                  </a>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  asChild
                >
                  <a href="/api/teams/employee-list.pdf" target="_blank" rel="noopener">
                    Employee PDF
                  </a>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  asChild
                >
                  <Link to="/teams/history">Manage History</Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  asChild
                >
                  <Link to="/teams/unbound">All Teams</Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  asChild
                >
                  <Link to="/teams/department">Department List</Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-md px-2.5 text-xs"
                  asChild
                >
                  <Link to="/teams/help">Help</Link>
                </Button>
              </div>

              {regionsQuery.isLoading && (
                <div className="space-y-2">
                  {["a", "b", "c", "d"].map((k) => (
                    <div key={k} className="h-8 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              )}
              {regionsQuery.isError && (
                <p className="text-destructive text-sm">Failed to load claims groups.</p>
              )}

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground text-xs">
                    <th className="px-1.5 pb-1 font-medium">Claim Group</th>
                    <th className="px-1.5 pb-1 text-right font-medium">Sort</th>
                    <th className="px-1.5 pb-1 text-center font-medium">Mgmt Group?</th>
                    <th className="px-1.5 pb-1 text-center font-medium">Backup?</th>
                    <th className="w-16 pb-1" />
                  </tr>
                </thead>
                <tbody>
                  {showAddRegion && (
                    <RegionRowEditor
                      form={regionForm}
                      onChange={setRegionForm}
                      onSave={() =>
                        addRegionMutation.mutate({
                          claimRegion: regionForm.claimRegion,
                          sortOrder: Number(regionForm.sortOrder),
                          isManagementGroup: regionForm.isManagementGroup,
                          isBackupHistory: regionForm.isBackupHistory,
                        })
                      }
                      onCancel={() => setShowAddRegion(false)}
                      isPending={addRegionMutation.isPending}
                    />
                  )}
                  {regionsQuery.data
                    ?.filter((r) => !pendingRegionIds.has(r.id))
                    .map((region) =>
                      editingRegionId === region.id ? (
                        <RegionRowEditor
                          key={region.id}
                          form={regionForm}
                          onChange={setRegionForm}
                          onSave={() =>
                            updateRegionMutation.mutate({
                              id: region.id,
                              claimRegion: regionForm.claimRegion,
                              sortOrder: Number(regionForm.sortOrder),
                              isManagementGroup: regionForm.isManagementGroup,
                              isBackupHistory: regionForm.isBackupHistory,
                            })
                          }
                          onCancel={() => setEditingRegionId(null)}
                          isPending={updateRegionMutation.isPending}
                        />
                      ) : (
                        <tr
                          key={region.id}
                          className={cn(
                            "cursor-pointer border-b last:border-0 hover:bg-muted/50",
                            selectedRegionId === region.id &&
                              "bg-primary/10 font-medium text-primary",
                          )}
                          onClick={() => handleSelectRegion(region)}
                        >
                          <td className="px-1.5 py-1">{region.claimRegion}</td>
                          <td className="px-1.5 py-1 text-right tabular-nums">
                            {region.sortOrder}
                          </td>
                          <td className="px-1.5 py-1 text-center">
                            <div className="inline-flex">
                              <CheckCell value={region.isManagementGroup} />
                            </div>
                          </td>
                          <td className="px-1.5 py-1 text-center">
                            <div className="inline-flex">
                              <CheckCell value={region.isBackupHistory} />
                            </div>
                          </td>
                          <td className="px-1.5 py-1 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                type="button"
                                aria-label="Edit"
                                title="Edit"
                                className="p-1 text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditRegion(region);
                                }}
                              >
                                <Pencil className="size-3.5" />
                              </button>
                              <button
                                type="button"
                                aria-label="Delete"
                                title="Delete"
                                className="p-1 text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPendingRegionDelete(region);
                                }}
                              >
                                <Trash2 className="size-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ),
                    )}
                  {regionsQuery.data?.length === 0 && !showAddRegion && (
                    <tr>
                      <td colSpan={5} className="py-4 text-center text-muted-foreground text-xs">
                        No claims groups yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Teams */}
          <section>
            <SectionBar
              title={teamsPanelTitle}
              subtitle={selectedRegionId ? `${teamsQuery.data?.length ?? 0} rows` : undefined}
            />
            <div className="rounded-b-md border border-t-0 bg-card p-3">
              {!selectedRegionId ? (
                <p className="py-4 text-center text-muted-foreground text-sm">
                  Select a claims group above to view its teams.
                </p>
              ) : (
                <>
                  <div className="mb-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-md px-2.5 text-xs"
                      onClick={() => {
                        setShowAddTeam(true);
                        setEditingTeamId(null);
                        setTeamForm({
                          claimRegionTeam: "",
                          manager: "",
                          sortOrder: "0",
                          isClaimTeam: true,
                        });
                      }}
                    >
                      <Plus className="size-3.5" />
                      Create
                    </Button>
                  </div>

                  {teamsQuery.isLoading && (
                    <div className="space-y-2">
                      {["a", "b", "c"].map((k) => (
                        <div key={k} className="h-8 animate-pulse rounded bg-muted" />
                      ))}
                    </div>
                  )}

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground text-xs">
                        <th className="px-1.5 pb-1 font-medium">Claim Team</th>
                        <th className="px-1.5 pb-1 font-medium">Manager</th>
                        <th className="px-1.5 pb-1 text-right font-medium">Sort</th>
                        <th className="px-1.5 pb-1 text-center font-medium">Claim Team?</th>
                        <th className="w-16 pb-1" />
                      </tr>
                    </thead>
                    <tbody>
                      {showAddTeam && selectedRegionId && (
                        <TeamRowEditor
                          form={teamForm}
                          onChange={setTeamForm}
                          onSave={() =>
                            addTeamMutation.mutate({
                              claimRegionTeam: teamForm.claimRegionTeam,
                              manager: teamForm.manager,
                              sortOrder: Number(teamForm.sortOrder),
                              isClaimTeam: teamForm.isClaimTeam,
                              claimRegionId: selectedRegionId,
                            })
                          }
                          onCancel={() => setShowAddTeam(false)}
                          isPending={addTeamMutation.isPending}
                        />
                      )}
                      {teamsQuery.data
                        ?.filter((t) => !pendingTeamIds.has(t.id))
                        .map((team) =>
                          editingTeamId === team.id ? (
                            <TeamRowEditor
                              key={team.id}
                              form={teamForm}
                              onChange={setTeamForm}
                              onSave={() =>
                                updateTeamMutation.mutate({
                                  id: team.id,
                                  claimRegionTeam: teamForm.claimRegionTeam,
                                  manager: teamForm.manager,
                                  sortOrder: Number(teamForm.sortOrder),
                                  isClaimTeam: teamForm.isClaimTeam,
                                })
                              }
                              onCancel={() => setEditingTeamId(null)}
                              isPending={updateTeamMutation.isPending}
                            />
                          ) : (
                            <tr
                              key={team.id}
                              className={cn(
                                "cursor-pointer border-b last:border-0 hover:bg-muted/50",
                                selectedTeamId === team.id &&
                                  "bg-primary/10 font-medium text-primary",
                              )}
                              onClick={() => handleSelectTeam(team)}
                            >
                              <td className="px-1.5 py-1">{team.claimRegionTeam}</td>
                              <td className="px-1.5 py-1 text-muted-foreground">{team.manager}</td>
                              <td className="px-1.5 py-1 text-right tabular-nums">
                                {team.sortOrder}
                              </td>
                              <td className="px-1.5 py-1 text-center">
                                <div className="inline-flex">
                                  <CheckCell value={team.isClaimTeam} />
                                </div>
                              </td>
                              <td className="px-1.5 py-1 text-right">
                                <div className="flex justify-end gap-0.5">
                                  <button
                                    type="button"
                                    aria-label="Edit"
                                    title="Edit"
                                    className="p-1 text-muted-foreground hover:text-foreground"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startEditTeam(team);
                                    }}
                                  >
                                    <Pencil className="size-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label="Delete"
                                    title="Delete"
                                    className="p-1 text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingTeamDelete(team);
                                    }}
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                      {teamsQuery.data?.length === 0 && !showAddTeam && (
                        <tr>
                          <td
                            colSpan={5}
                            className="py-4 text-center text-muted-foreground text-xs"
                          >
                            No teams in {groupLabel} yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </section>
        </div>

        {/* ─── RIGHT COLUMN: Members ─── */}
        <div className="lg:col-span-8">
          <SectionBar
            title="Members"
            subtitle={selectedTeamId ? `${membersQuery.data?.length ?? 0} rows` : undefined}
          />
          <div className="rounded-b-md border border-t-0 bg-card p-3">
            {!selectedTeamId ? (
              <p className="py-4 text-center text-muted-foreground text-sm">
                Select a team from the left to view its members.
              </p>
            ) : (
              <>
                <div className="mb-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 rounded-md px-2.5 text-xs"
                    onClick={() => {
                      setShowAddUser(true);
                      setUserForm({ userId: "" });
                    }}
                  >
                    <Plus className="size-3.5" />
                    Create
                  </Button>
                </div>

                {membersQuery.isLoading && (
                  <div className="space-y-2">
                    {["a", "b", "c", "d"].map((k) => (
                      <div key={k} className="h-8 animate-pulse rounded bg-muted" />
                    ))}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground text-xs">
                        <th className="px-1.5 pb-1 font-medium">User ID</th>
                        <th className="px-1.5 pb-1 font-medium">Name</th>
                        <th className="px-1.5 pb-1 font-medium">Title</th>
                        <th className="px-1.5 pb-1 font-medium">Phone</th>
                        <th className="px-1.5 pb-1 font-medium">Ext.</th>
                        <th className="px-1.5 pb-1 font-medium">Mobile</th>
                        <th className="px-1.5 pb-1 font-medium">Location</th>
                        <th className="px-1.5 pb-1 font-medium">Email</th>
                        <th className="px-1.5 pb-1 text-center font-medium">Exclude PDF</th>
                        <th className="w-10 pb-1" />
                      </tr>
                    </thead>
                    <tbody>
                      {showAddUser && selectedTeamId && (
                        <tr className="border-b">
                          <td className="px-1.5 py-1" colSpan={10}>
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="User ID (sAMAccountName)"
                                list={USER_LIST_ID}
                                value={userForm.userId}
                                onChange={(e) => setUserForm({ userId: e.target.value })}
                                className="h-8 max-w-xs"
                              />
                              <Button
                                size="sm"
                                className="h-7"
                                disabled={addUserMutation.isPending || !userForm.userId.trim()}
                                onClick={() =>
                                  addUserMutation.mutate({
                                    userId: userForm.userId,
                                    claimRegionTeamId: selectedTeamId,
                                  })
                                }
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7"
                                onClick={() => setShowAddUser(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {membersQuery.data
                        ?.filter((m) => !pendingMemberIds.has(m.id))
                        .map((m) => (
                          <tr key={m.id} className="border-b last:border-0 hover:bg-muted/30">
                            <td className="px-1.5 py-1 font-medium">{m.userId}</td>
                            <td className="px-1.5 py-1">{m.firstLastName ?? "—"}</td>
                            <td className="px-1.5 py-1 text-muted-foreground">{m.title ?? "—"}</td>
                            <td className="px-1.5 py-1 text-muted-foreground">
                              {m.phoneNumber ? (
                                <a
                                  href={`tel:${m.phoneNumber}`}
                                  className="text-primary hover:underline"
                                >
                                  {m.phoneNumber}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-1.5 py-1 text-muted-foreground tabular-nums">
                              {m.extension ?? "—"}
                            </td>
                            <td className="px-1.5 py-1 text-muted-foreground">
                              {m.mobilePhone ? (
                                <a
                                  href={`tel:${m.mobilePhone}`}
                                  className="text-primary hover:underline"
                                >
                                  {m.mobilePhone}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-1.5 py-1 text-muted-foreground">
                              {m.location ?? "—"}
                            </td>
                            <td className="px-1.5 py-1 text-muted-foreground">
                              {m.email ? (
                                <a
                                  href={`mailto:${m.email}`}
                                  className="text-primary hover:underline"
                                >
                                  {m.email}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-1.5 py-1 text-center">
                              <div className="inline-flex">
                                <CheckCell value={m.isExcludeFromPdf} />
                              </div>
                            </td>
                            <td className="px-1.5 py-1 text-right">
                              <button
                                type="button"
                                aria-label="Remove member"
                                title="Remove member"
                                className="p-1 text-muted-foreground hover:text-destructive"
                                onClick={() =>
                                  setPendingMemberDelete({ id: m.id, userId: m.userId })
                                }
                              >
                                <Trash2 className="size-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      {membersQuery.data?.length === 0 && !showAddUser && (
                        <tr>
                          <td
                            colSpan={10}
                            className="py-4 text-center text-muted-foreground text-xs"
                          >
                            No members on this team.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        <ConfirmDialog
          open={pendingRegionDelete !== null}
          onOpenChange={(o) => {
            if (!o) setPendingRegionDelete(null);
          }}
          title="Delete claims group?"
          description={
            pendingRegionDelete
              ? `This will remove "${pendingRegionDelete.claimRegion}" and all its teams. You'll have 10 seconds to undo from the toast.`
              : undefined
          }
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            if (pendingRegionDelete) {
              enqueueRegionDelete(pendingRegionDelete);
              if (selectedRegionId === pendingRegionDelete.id) {
                setSelectedRegionId(null);
                setSelectedTeamId(null);
              }
            }
            setPendingRegionDelete(null);
          }}
        />

        <ConfirmDialog
          open={pendingTeamDelete !== null}
          onOpenChange={(o) => {
            if (!o) setPendingTeamDelete(null);
          }}
          title="Delete team?"
          description={
            pendingTeamDelete
              ? `This will remove team "${pendingTeamDelete.claimRegionTeam}" and unassign its members. You'll have 10 seconds to undo from the toast.`
              : undefined
          }
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            if (pendingTeamDelete) {
              enqueueTeamDelete(pendingTeamDelete);
              if (selectedTeamId === pendingTeamDelete.id) setSelectedTeamId(null);
            }
            setPendingTeamDelete(null);
          }}
        />

        <ConfirmDialog
          open={pendingMemberDelete !== null}
          onOpenChange={(o) => {
            if (!o) setPendingMemberDelete(null);
          }}
          title="Remove member?"
          description={
            pendingMemberDelete
              ? `Remove ${pendingMemberDelete.userId} from this team. You'll have 10 seconds to undo from the toast.`
              : undefined
          }
          confirmLabel="Remove"
          destructive
          onConfirm={() => {
            if (pendingMemberDelete) enqueueMemberDelete(pendingMemberDelete);
            setPendingMemberDelete(null);
          }}
        />
      </div>
    </div>
  );
}

// ── Row-editor sub-components ──

function RegionRowEditor({
  form,
  onChange,
  onSave,
  onCancel,
  isPending,
}: {
  form: {
    claimRegion: string;
    sortOrder: string;
    isManagementGroup: boolean;
    isBackupHistory: boolean;
  };
  onChange: (f: typeof form | ((prev: typeof form) => typeof form)) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <tr className="border-b bg-accent/40">
      <td className="px-1.5 py-1">
        <Input
          className="h-7 text-sm"
          placeholder="Claims group name"
          value={form.claimRegion}
          onChange={(e) => onChange({ ...form, claimRegion: e.target.value })}
        />
      </td>
      <td className="px-1.5 py-1">
        <Input
          className="h-7 w-16 text-right text-sm"
          type="number"
          value={form.sortOrder}
          onChange={(e) => onChange({ ...form, sortOrder: e.target.value })}
        />
      </td>
      <td className="px-1.5 py-1 text-center">
        <Checkbox
          checked={form.isManagementGroup}
          onCheckedChange={(checked) => onChange({ ...form, isManagementGroup: checked === true })}
          aria-label="Management group"
        />
      </td>
      <td className="px-1.5 py-1 text-center">
        <Checkbox
          checked={form.isBackupHistory}
          onCheckedChange={(checked) => onChange({ ...form, isBackupHistory: checked === true })}
          aria-label="Backup history"
        />
      </td>
      <td className="px-1.5 py-1 text-right">
        <div className="flex justify-end gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Save"
            title="Save"
            className="size-7 p-0 text-primary hover:text-primary/80"
            disabled={isPending || !form.claimRegion.trim()}
            onClick={onSave}
          >
            <Check className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Cancel"
            title="Cancel"
            className="size-7 p-0"
            onClick={onCancel}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function TeamRowEditor({
  form,
  onChange,
  onSave,
  onCancel,
  isPending,
}: {
  form: { claimRegionTeam: string; manager: string; sortOrder: string; isClaimTeam: boolean };
  onChange: (f: typeof form | ((prev: typeof form) => typeof form)) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <tr className="border-b bg-accent/40">
      <td className="px-1.5 py-1">
        <Input
          className="h-7 text-sm placeholder:text-muted-foreground/50 placeholder:italic"
          placeholder="Team Name"
          value={form.claimRegionTeam}
          onChange={(e) => onChange({ ...form, claimRegionTeam: e.target.value })}
        />
      </td>
      <td className="px-1.5 py-1">
        <Input
          className="h-7 text-sm placeholder:text-muted-foreground/50 placeholder:italic"
          placeholder="Manager Name"
          list={MANAGER_LIST_ID}
          value={form.manager}
          onChange={(e) => onChange({ ...form, manager: e.target.value })}
        />
      </td>
      <td className="px-1.5 py-1">
        <Input
          className="h-7 w-16 text-right text-sm"
          type="number"
          value={form.sortOrder}
          onChange={(e) => onChange({ ...form, sortOrder: e.target.value })}
        />
      </td>
      <td className="px-1.5 py-1 text-center">
        <Checkbox
          checked={form.isClaimTeam}
          onCheckedChange={(checked) => onChange({ ...form, isClaimTeam: checked === true })}
          aria-label="Claim team"
        />
      </td>
      <td className="px-1.5 py-1 text-right">
        <div className="flex justify-end gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Save"
            title="Save"
            className="size-7 p-0 text-primary hover:text-primary/80"
            disabled={isPending || !form.claimRegionTeam.trim() || !form.manager.trim()}
            onClick={onSave}
          >
            <Check className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Cancel"
            title="Cancel"
            className="size-7 p-0"
            onClick={onCancel}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
